import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  AnimatePresence,
  motion,
} from "framer-motion";

import siteConfig from "../../../site/config/site";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  PREMIUM INTRO MODAL
  ============================================================

  Asiakasystävällinen myyntimodal Photography Premium -demolle.

  Modal kertoo:
  - mitä kaikissa toteutuksissa on mukana
  - Kevyt / Premium / Räätälöity -vaihtoehdot
  - hakukonenäkyvyyden laajennukset
  - kieliversiot
  - lisäpalvelut

  Demon kuvat, tekstit, hinnastosisällöt ja Studio-sivu ovat
  esimerkkisisältöä. Varsinaisessa toteutuksessa ne korvataan
  asiakkaan omalla sisällöllä ja brändillä.

  Uusi kieli voidaan lisätä copy-objektiin esimerkiksi
  sv-, de- tai es-kieliversiona.
*/

const copy = {
  fi: {
    eyebrow:
      "Photography Premium · Demo",

    title:
      "Valmis verkkosivupohja. Sinun brändisi näköiseksi.",

    description:
      "Photography Premium on valokuvaajille rakennettu laadukas verkkosivukokonaisuus. Ulkoasu, kuvat, tekstit, palvelut, värit, animaatiot ja sisältö mukautetaan aina asiakkaan omaan brändiin.",

    pricingNote:
      "Pakettien tarkoitus on helpottaa sopivan kokonaisuuden valintaa. Sisältöjä ja ominaisuuksia voidaan lisätä, poistaa tai vaihtaa tarpeen mukaan.",

    demoNote:
      "Tässä demossa käytetyt kuvat, tekstit, hinnat ja Studio-sivu ovat esimerkkisisältöä. Varsinaisessa toteutuksessa ne korvataan asiakkaan omilla kuvilla, sisällöillä ja tiedoilla.",

    sharedEyebrow:
      "Kaikissa sivustoissa",

    sharedFeatures: [
      "Responsiivinen mobiili- ja desktop-toteutus",
      "Nopea ja selkeä rakenne",
      "Portfolio",
      "Yhteydenottolomake",
      "Pehmeät animaatiot",
      "WordPress-hallinta",
      "Hakukoneystävällinen perusrakenne",
    ],

    tiersEyebrow:
      "Toteutusvaihtoehdot",

    recommended:
      "Laajempi kokonaisuus",

    tiers: [
      {
        id: "essential",

        name:
          "Kevyt",

        prefix:
          "alk.",

        price:
          "1 490 €",

        description:
          "Valmis ammattimainen sivusto, joka sisältää kaiken olennaisen oman työn ja palveluiden esittelyyn.",

        features: [
          "Etusivu",
          "Portfolio",
          "Palvelut / kuvaukset",
          "Hinnasto tarvittaessa",
          "Kuvaaja / esittely",
          "Yhteys + lomake",
          "Some- ja yhteystiedot",
          "1 kieliversio",
          "WordPress-sisällönhallinta",
          "Hakukoneoptimoinnin perusteet",
        ],
      },

      {
        id: "premium",

        name:
          "Premium",

        prefix:
          "alk.",

        price:
          "2 490 €",

        description:
          "Kun sivustolta halutaan enemmän sisältöä, näkyvyyttä ja toimintoja sekä vahvempi pohja hakukoneista löytymiselle.",

        featured:
          true,

        features: [
          "Kaikki Kevyt-paketista",
          "2 kieliversiota",
          "Asiakaspalautteet",
          "UKK",
          "Studio / toimitila -sivu tarvittaessa",
          "Laajempi sisällönhallinta",
          "Lisätoiminnot ja CTA:t",
          "Kävijäseuranta",
          "Evästehallinta",
          "Laajempi hakukonenäkyvyys",
          "3 SEO-ländäriä",
        ],
      },

      {
        id: "custom",

        name:
          "Räätälöity",

        prefix:
          "alk.",

        price:
          "3 490 €",

        description:
          "Yksilöllisempi kokonaisuus, kun valmiin rakenteen lisäksi tarvitaan omia sivuja, toimintoja tai integraatioita.",

        features: [
          "Kaikki Premium-paketista",
          "Useita kieliversioita",
          "Yksilölliset sivut ja osiot",
          "5–10+ SEO-ländäriä",
          "Laajempi näkyvyyskokonaisuus",
          "Ajanvarausratkaisut",
          "Räätälöidyt integraatiot",
          "Erikoislomakkeet",
          "Laajempi sisältöjen siirto",
        ],
      },
    ],

    seoEyebrow:
      "Näkyvyys Googlessa",

    seoTitle:
      "Sivusto voidaan rakentaa myös aktiivisesti uusia asiakkaita varten.",

    seoDescription:
      "Premiumissa sivuston sisältöä ja rakennetta kehitetään tavallista laajemmin hakukonenäkyvyyttä varten. Palvelut, kuvat ja sivut rakennetaan niin, että potentiaalisen asiakkaan on helpompi löytää yritys oikeilla hakusanoilla.",

    seoLandingTitle:
      "SEO-ländärit",

    seoLandingDescription:
      "SEO-ländäri on erillinen palvelu- tai paikkasivu, joka tehdään tiettyä hakua varten. Premiumiin kuuluu kolme ländäriä ja Räätälöityyn 5–10 tai enemmän tarpeen mukaan.",

    seoExamples:
      "Esimerkiksi: Hääkuvaaja Helsinki · Yrityskuvaaja Espoo · Henkilökuvaus Vantaa",

    addonsEyebrow:
      "Lisättävissä tarpeen mukaan",

    addons: [
      "Lisäkieli",
      "Lisä-SEO-ländärit",
      "Studio / toimitila -sivu",
      "Ajanvaraus",
      "Lisäsivut",
      "Lisälomakkeet",
      "Integraatiot",
      "Sisältöjen siirto",
      "Jatkuva ylläpito",
    ],

    addonsDescription:
      "Paketteja voidaan yhdistellä. Esimerkiksi Kevyeen voidaan lisätä toinen kieli tai muutama SEO-ländäri ilman että koko toteutus täytyy vaihtaa Premiumiksi.",

    languageEyebrow:
      "Kieliversiot",

    languageTitle:
      "Sivusto ei ole sidottu suomeen ja englantiin.",

    languageDescription:
      "Kieliversiot voidaan tehdä asiakkaan tarpeen mukaan. Esimerkiksi suomi + ruotsi, ruotsi + englanti tai useampi kieli ovat mahdollisia. Jokainen kieliversio rakennetaan osaksi samaa hallittavaa sivustoa.",

    explore:
      "Tutustu demoon",

    contact:
      "Pyydä tarjous",

    footerNote:
      "Hinnat ovat alkaen-hintoja. Lopullinen hinta määräytyy sivumäärän, kieliversioiden, sisältöjen ja valittujen lisäominaisuuksien mukaan.",

    close:
      "Sulje esittely",
  },

  en: {
    eyebrow:
      "Photography Premium · Demo",

    title:
      "A ready-made foundation. Tailored to your brand.",

    description:
      "Photography Premium is a high-quality website system created for photographers. The imagery, copy, services, colours, animation and visual identity are always adapted to the client's own brand.",

    pricingNote:
      "The packages make it easier to choose the right scope. Features and content can be added, removed or exchanged depending on the project.",

    demoNote:
      "The photographs, copy, prices and Studio page shown in this demo are example content. In the final implementation they are replaced with the client's own imagery, content and information.",

    sharedEyebrow:
      "Included in every website",

    sharedFeatures: [
      "Responsive mobile and desktop implementation",
      "Fast and clear structure",
      "Portfolio",
      "Contact form",
      "Soft animation",
      "WordPress management",
      "Search-friendly foundation",
    ],

    tiersEyebrow:
      "Implementation options",

    recommended:
      "Extended package",

    tiers: [
      {
        id: "essential",

        name:
          "Essential",

        prefix:
          "from",

        price:
          "€1,490",

        description:
          "A complete professional website containing the essential pages needed to present your work and services.",

        features: [
          "Home",
          "Portfolio",
          "Services / sessions",
          "Pricing when needed",
          "About",
          "Contact + form",
          "Social and contact details",
          "1 language version",
          "WordPress content management",
          "Core search optimisation",
        ],
      },

      {
        id: "premium",

        name:
          "Premium",

        prefix:
          "from",

        price:
          "€2,490",

        description:
          "For photographers who want more content, functionality and a stronger foundation for attracting new customers through search.",

        featured:
          true,

        features: [
          "Everything in Essential",
          "2 language versions",
          "Testimonials",
          "FAQ",
          "Studio / premises page when needed",
          "Extended content management",
          "Additional calls to action",
          "Visitor analytics",
          "Cookie management",
          "Extended search visibility",
          "3 SEO landing pages",
        ],
      },

      {
        id: "custom",

        name:
          "Custom",

        prefix:
          "from",

        price:
          "€3,490",

        description:
          "A more individual website when the project needs custom pages, functionality or integrations beyond the premium foundation.",

        features: [
          "Everything in Premium",
          "Multiple language versions",
          "Custom pages and sections",
          "5–10+ SEO landing pages",
          "Extended visibility strategy",
          "Booking solutions",
          "Custom integrations",
          "Specialised forms",
          "Extended content migration",
        ],
      },
    ],

    seoEyebrow:
      "Visibility in search",

    seoTitle:
      "The website can also be built to actively attract new clients.",

    seoDescription:
      "Premium includes a broader approach to search visibility. Services, images and page content are structured to help potential clients discover the business through relevant searches.",

    seoLandingTitle:
      "SEO landing pages",

    seoLandingDescription:
      "An SEO landing page is a dedicated service or location page designed around a specific search. Premium includes three landing pages and Custom includes 5–10 or more depending on the project.",

    seoExamples:
      "For example: Wedding photographer Helsinki · Business photographer Espoo · Portrait photography Vantaa",

    addonsEyebrow:
      "Optional additions",

    addons: [
      "Additional language",
      "Additional SEO landing pages",
      "Studio / premises page",
      "Booking",
      "Additional pages",
      "Additional forms",
      "Integrations",
      "Content migration",
      "Ongoing maintenance",
    ],

    addonsDescription:
      "Packages can be combined. For example, an additional language or a few SEO landing pages can be added to Essential without requiring the entire project to move to Premium.",

    languageEyebrow:
      "Languages",

    languageTitle:
      "The website is not limited to Finnish and English.",

    languageDescription:
      "Language versions can be selected according to the client's needs. Finnish and Swedish, Swedish and English, or several other languages can all be supported within the same manageable website.",

    explore:
      "Explore the demo",

    contact:
      "Request a quote",

    footerNote:
      "Prices are starting prices. The final price depends on the number of pages, language versions, content and selected additional functionality.",

    close:
      "Close introduction",
  },
};

export default function PremiumIntroModal({
  language = "fi",
}) {
  const config =
    siteConfig.product
      ?.demoModal || {};

  const storageKey =
    config.storageKey ||
    "photography_premium_demo_intro_v4";

  const defaultLanguage =
    siteConfig.languages
      ?.default ||
    "fi";

  const content =
    copy[language] ||
    copy[defaultLanguage] ||
    copy.fi;

  const [
    isOpen,
    setIsOpen,
  ] = useState(false);

  /*
    ============================================================
    OPEN ONCE PER SESSION
    ============================================================
  */

  useEffect(() => {
    if (
      config.enabled === false
    ) {
      return undefined;
    }

    const delay =
      Number(
        config.delayMs ||
        0
      );

    const timer =
      window.setTimeout(
        () => {
          let hasSeen =
            false;

          try {
            hasSeen =
              window.sessionStorage.getItem(
                storageKey
              ) === "1";
          } catch {
            hasSeen =
              false;
          }

          if (
            !hasSeen
          ) {
            setIsOpen(true);
          }
        },
        delay
      );

    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [
    config.delayMs,
    config.enabled,
    storageKey,
  ]);

  const rememberSeen =
    useCallback(() => {
      try {
        window.sessionStorage.setItem(
          storageKey,
          "1"
        );
      } catch {
        // Modal toimii myös ilman sessionStoragea.
      }
    }, [
      storageKey,
    ]);

  const closeModal =
    useCallback(() => {
      rememberSeen();
      setIsOpen(false);
    }, [
      rememberSeen,
    ]);

  useEffect(() => {
    if (
      !isOpen
    ) {
      return undefined;
    }

    const previousOverflow =
      document.body.style
        .overflow;

    document.body.style
      .overflow =
      "hidden";

    function handleKeyDown(
      event
    ) {
      if (
        event.key ===
        "Escape"
      ) {
        closeModal();
      }
    }

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.body.style
        .overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [
    closeModal,
    isOpen,
  ]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="
            fixed
            inset-0
            z-[120]
            flex
            items-end
            justify-center
            overflow-y-auto
            bg-black/60
            p-3
            backdrop-blur-sm

            sm:items-center
            sm:p-6
          "
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
          }}
          transition={{
            duration: 0.28,
          }}
          onMouseDown={(
            event
          ) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              closeModal();
            }
          }}
        >
          <motion.section
            role="dialog"
            aria-modal="true"
            aria-labelledby="premium-demo-title"
            className="
              relative
              max-h-[calc(100dvh-1.5rem)]
              w-full
              max-w-7xl
              overflow-x-hidden
              overflow-y-auto
              border
              border-border
              bg-canvas
              text-text
              shadow-2xl

              sm:max-h-[calc(100dvh-3rem)]
            "
            initial={{
              opacity: 0,
              y: 28,
              scale: 0.985,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              y: 16,
              scale: 0.99,
            }}
            transition={{
              duration: 0.5,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <button
              type="button"
              onClick={
                closeModal
              }
              className="
                absolute
                right-4
                top-4
                z-30
                flex
                h-11
                w-11
                items-center
                justify-center
                rounded-full
                border
                border-border
                bg-canvas/90
                text-text
                backdrop-blur-md
                transition
                duration-300
                hover:border-text

                sm:right-5
                sm:top-5
              "
              aria-label={
                content.close
              }
            >
              <span
                className="
                  text-xl
                  font-light
                  leading-none
                "
                aria-hidden="true"
              >
                ×
              </span>
            </button>

            {/* =================================================
                INTRO
                ================================================= */}

            <div
              className="
                border-b
                border-border
                px-6
                pb-9
                pt-16

                sm:px-10
                sm:pb-11
                sm:pt-16

                lg:grid
                lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.6fr)]
                lg:gap-20
                lg:px-14
                lg:py-12
              "
            >
              <div>
                <p
                  className="
                    mb-6
                    text-[9px]
                    font-semibold
                    uppercase
                    tracking-[0.28em]
                    text-muted

                    sm:text-[10px]
                  "
                >
                  {
                    content.eyebrow
                  }
                </p>

                <h2
                  id="premium-demo-title"
                  className="
                    m-0
                    max-w-4xl
                    font-display
                    text-[clamp(2.3rem,11vw,2.9rem)]
                    font-normal
                    leading-[0.92]
                    tracking-[-0.045em]

                    sm:text-[clamp(2.8rem,5vw,5.6rem)]
                    sm:leading-[0.89]
                    sm:tracking-[-0.05em]
                  "
                >
                  {
                    content.title
                  }
                </h2>

                <p
                  className="
                    mb-0
                    mt-8
                    max-w-3xl
                    text-sm
                    leading-7
                    text-muted

                    sm:text-base
                    sm:leading-8
                  "
                >
                  {
                    content.description
                  }
                </p>

                <p
                  className="
                    mb-0
                    mt-4
                    max-w-3xl
                    text-sm
                    leading-7
                    text-muted
                  "
                >
                  {
                    content.pricingNote
                  }
                </p>

                <p
                  className="
                    mb-0
                    mt-5
                    max-w-3xl
                    border-l
                    border-text
                    pl-4
                    text-xs
                    leading-6
                    text-muted
                  "
                >
                  {
                    content.demoNote
                  }
                </p>
              </div>

              <div
                className="
                  mt-10

                  lg:mt-2
                "
              >
                <p
                  className="
                    m-0
                    text-[9px]
                    font-semibold
                    uppercase
                    tracking-[0.24em]
                    text-muted
                  "
                >
                  {
                    content.sharedEyebrow
                  }
                </p>

                <div
                  className="
                    mt-5
                    flex
                    flex-wrap
                    gap-2
                  "
                >
                  {content.sharedFeatures.map(
                    (feature) => (
                      <span
                        key={
                          feature
                        }
                        className="
                          rounded-full
                          border
                          border-border
                          px-3
                          py-2
                          text-[8px]
                          font-medium
                          uppercase
                          tracking-[0.14em]
                          text-muted

                          sm:text-[9px]
                        "
                      >
                        {
                          feature
                        }
                      </span>
                    )
                  )}
                </div>
              </div>
            </div>

            {/* =================================================
                PACKAGES
                ================================================= */}

            <div
              className="
                bg-surface
                px-6
                py-9

                sm:px-10
                sm:py-11

                lg:px-14
                lg:py-12
              "
            >
              <p
                className="
                  m-0
                  text-[9px]
                  font-semibold
                  uppercase
                  tracking-[0.26em]
                  text-muted

                  sm:text-[10px]
                "
              >
                {
                  content.tiersEyebrow
                }
              </p>

              <div
                className="
                  mt-7
                  grid
                  gap-4

                  lg:grid-cols-3
                "
              >
                {content.tiers.map(
                  (tier) => (
                    <article
                      key={
                        tier.id
                      }
                      className={`
                        relative
                        flex
                        flex-col
                        border
                        p-6

                        sm:p-7

                        ${
                          tier.featured
                            ? "border-text bg-canvas"
                            : "border-border bg-canvas/40"
                        }
                      `}
                    >
                      {tier.featured && (
                        <span
                          className="
                            mb-5
                            inline-flex
                            w-fit
                            rounded-full
                            bg-text
                            px-3
                            py-1.5
                            text-[8px]
                            font-semibold
                            uppercase
                            tracking-[0.16em]
                            text-canvas
                          "
                        >
                          {
                            content.recommended
                          }
                        </span>
                      )}

                      <div
                        className="
                          flex
                          flex-col
                          gap-3

                          sm:flex-row
                          sm:items-start
                          sm:justify-between
                          sm:gap-5
                        "
                      >
                        <h3
                          className="
                            m-0
                            font-display
                            text-[2rem]
                            font-normal
                            leading-none
                            tracking-[-0.035em]
                          "
                        >
                          {
                            tier.name
                          }
                        </h3>

                        <div
                          className="
                            shrink-0
                            text-left

                            sm:text-right
                          "
                        >
                          <p
                            className="
                              m-0
                              text-[8px]
                              font-semibold
                              uppercase
                              tracking-[0.16em]
                              text-muted
                            "
                          >
                            {
                              tier.prefix
                            }
                          </p>

                          <p
                            className="
                              m-0
                              mt-1
                              whitespace-nowrap
                              font-display
                              text-[1.65rem]
                              leading-none
                              tracking-[-0.035em]
                            "
                          >
                            {
                              tier.price
                            }
                          </p>
                        </div>
                      </div>

                      <p
                        className="
                          mb-0
                          mt-5
                          text-xs
                          leading-5
                          text-muted
                        "
                      >
                        {
                          tier.description
                        }
                      </p>

                      <ul
                        className="
                          mb-0
                          mt-6
                          border-t
                          border-border
                          p-0
                        "
                      >
                        {tier.features.map(
                          (feature) => (
                            <li
                              key={
                                feature
                              }
                              className="
                                flex
                                gap-3
                                border-b
                                border-border
                                py-3
                                text-xs
                                leading-5
                                text-text
                              "
                            >
                              <span
                                className="
                                  shrink-0
                                  text-muted
                                "
                                aria-hidden="true"
                              >
                                +
                              </span>

                              <span>
                                {
                                  feature
                                }
                              </span>
                            </li>
                          )
                        )}
                      </ul>
                    </article>
                  )
                )}
              </div>
            </div>

            {/* =================================================
                SEARCH VISIBILITY
                ================================================= */}

            <div
              className="
                grid
                border-t
                border-border

                lg:grid-cols-2
              "
            >
              <div
                className="
                  px-6
                  py-9

                  sm:px-10

                  lg:px-14
                  lg:py-11
                "
              >
                <p
                  className="
                    m-0
                    text-[9px]
                    font-semibold
                    uppercase
                    tracking-[0.25em]
                    text-muted
                  "
                >
                  {
                    content.seoEyebrow
                  }
                </p>

                <h3
                  className="
                    mb-0
                    mt-5
                    max-w-xl
                    font-display
                    text-[clamp(1.9rem,9vw,2.25rem)]
                    font-normal
                    leading-[0.98]
                    tracking-[-0.035em]

                    sm:text-[clamp(2rem,3.2vw,3.4rem)]
                    sm:leading-[0.95]
                    sm:tracking-[-0.04em]
                  "
                >
                  {
                    content.seoTitle
                  }
                </h3>

                <p
                  className="
                    mb-0
                    mt-6
                    max-w-2xl
                    text-sm
                    leading-7
                    text-muted
                  "
                >
                  {
                    content.seoDescription
                  }
                </p>

                <h4
                  className="
                    mb-0
                    mt-8
                    text-[10px]
                    font-semibold
                    uppercase
                    tracking-[0.2em]
                    text-text
                  "
                >
                  {
                    content.seoLandingTitle
                  }
                </h4>

                <p
                  className="
                    mb-0
                    mt-3
                    max-w-2xl
                    text-sm
                    leading-7
                    text-muted
                  "
                >
                  {
                    content.seoLandingDescription
                  }
                </p>

                <p
                  className="
                    mb-0
                    mt-5
                    border-l
                    border-text
                    pl-4
                    text-xs
                    leading-6
                    text-muted
                  "
                >
                  {
                    content.seoExamples
                  }
                </p>
              </div>

              {/* =================================================
                  LANGUAGES
                  ================================================= */}

              <div
                className="
                  border-t
                  border-border
                  bg-surface
                  px-6
                  py-9

                  sm:px-10

                  lg:border-l
                  lg:border-t-0
                  lg:px-14
                  lg:py-11
                "
              >
                <p
                  className="
                    m-0
                    text-[9px]
                    font-semibold
                    uppercase
                    tracking-[0.25em]
                    text-muted
                  "
                >
                  {
                    content.languageEyebrow
                  }
                </p>

                <h3
                  className="
                    mb-0
                    mt-5
                    max-w-xl
                    font-display
                    text-[clamp(1.9rem,9vw,2.25rem)]
                    font-normal
                    leading-[0.98]
                    tracking-[-0.035em]

                    sm:text-[clamp(2rem,3.2vw,3.4rem)]
                    sm:leading-[0.95]
                    sm:tracking-[-0.04em]
                  "
                >
                  {
                    content.languageTitle
                  }
                </h3>

                <p
                  className="
                    mb-0
                    mt-6
                    max-w-xl
                    text-sm
                    leading-7
                    text-muted
                  "
                >
                  {
                    content.languageDescription
                  }
                </p>
              </div>
            </div>

            {/* =================================================
                ADDONS
                ================================================= */}

            <div
              className="
                border-t
                border-border
                px-6
                py-8

                sm:px-10

                lg:px-14
                lg:py-10
              "
            >
              <p
                className="
                  m-0
                  text-[9px]
                  font-semibold
                  uppercase
                  tracking-[0.25em]
                  text-muted
                "
              >
                {
                  content.addonsEyebrow
                }
              </p>

              <div
                className="
                  mt-5
                  flex
                  flex-wrap
                  gap-2
                "
              >
                {content.addons.map(
                  (addon) => (
                    <span
                      key={
                        addon
                      }
                      className="
                        rounded-full
                        border
                        border-border
                        px-3
                        py-2
                        text-[8px]
                        font-medium
                        uppercase
                        tracking-[0.14em]
                        text-muted

                        sm:text-[9px]
                      "
                    >
                      + {
                        addon
                      }
                    </span>
                  )
                )}
              </div>

              <p
                className="
                  mb-0
                  mt-6
                  max-w-3xl
                  text-sm
                  leading-7
                  text-muted
                "
              >
                {
                  content.addonsDescription
                }
              </p>
            </div>

            {/* =================================================
                CTA
                ================================================= */}

            <div
              className="
                flex
                flex-col
                gap-5
                border-t
                border-border
                bg-surface
                px-6
                py-7

                sm:px-10

                md:flex-row
                md:items-center
                md:justify-between

                lg:px-14
              "
            >
              <p
                className="
                  m-0
                  max-w-2xl
                  text-[10px]
                  leading-5
                  text-muted
                "
              >
                {
                  content.footerNote
                }
              </p>

              <div
                className="
                  flex
                  shrink-0
                  flex-col
                  gap-3

                  sm:flex-row
                "
              >
                <button
                  type="button"
                  onClick={
                    closeModal
                  }
                  className="
                    inline-flex
                    min-h-12
                    items-center
                    justify-center
                    bg-text
                    px-6
                    py-3
                    text-[9px]
                    font-semibold
                    uppercase
                    tracking-[0.18em]
                    text-canvas
                    transition-opacity
                    hover:opacity-80

                    sm:text-[10px]
                  "
                >
                  {
                    content.explore
                  }
                </button>

               <a
                  href={`mailto:astenius@live.com?subject=${encodeURIComponent(
                    language === "en"
                      ? "Photography Premium – Quote request"
                      : "Photography Premium – Tarjouspyyntö"
                  )}&body=${encodeURIComponent(
                    language === "en"
                      ? "Hi Arttu,\n\nI am interested in the Photography Premium website solution.\n\n"
                      : "Hei Arttu,\n\nOlen kiinnostunut Photography Premium -verkkosivuratkaisusta.\n\n"
                  )}`}
                  onClick={
                    closeModal
                  }
                  className="
                    inline-flex
                    min-h-12
                    items-center
                    justify-center
                    border
                    border-border
                    px-6
                    py-3
                    text-[9px]
                    font-semibold
                    uppercase
                    tracking-[0.18em]
                    text-text
                    no-underline
                    transition
                    hover:border-text

                    sm:text-[10px]
                  "
                >
                  {
                    content.contact
                  }
                </a>
              </div>
            </div>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
}